import type { UseMutationResult, UseMutationOptions } from 'react-query';
import { isSandboxEnvironment } from '../env-indicator';
import { toastFromPromise } from '../vy-toasts';
import type { ToastRenderType } from '../vy-toasts/vy-toast-types';
import { debounce } from 'lodash';
import { AsyncFunction, getRequiredMapValue } from '@vy/utils';

export async function runMutation<MutateResponse, MutateVariables, Error>(
  mutation: UseMutationResult<MutateResponse, Error, MutateVariables>,
  variables: MutateVariables,
  vyToastOptions: ToastRenderType<MutateResponse, Error>,
  options?: UseMutationOptions<MutateResponse, unknown, MutateVariables>,
) {
  if (isSandboxEnvironment()) {
    await mutation.mutateAsync(variables, options);

    return undefined;
  }

  return toastFromPromise(mutation.mutateAsync(variables, options), vyToastOptions);
}

const debouncedFunctions = new Map<string, AsyncFunction>();

export function runDebouncedMutation<MutateResponse, MutateVariables, Error>(
  key: string,
  mutation: UseMutationResult<MutateResponse, Error, MutateVariables>,
  variables: MutateVariables,
  vyToastOptions: ToastRenderType<MutateResponse, Error>,
  options?: UseMutationOptions<MutateResponse, unknown, MutateVariables>,
  wait = 1000,
  leadingEdge = true,
) {
  const functionKey = `debounce-${key}`;

  if (!debouncedFunctions.has(functionKey)) {
    const debouncedFn = debounce(
      async (
        mut: typeof mutation,
        vars: typeof variables,
        toastOpts: typeof vyToastOptions,
        opts?: typeof options,
      ) => {
        try {
          await runMutation(mut, vars, toastOpts, opts);
        } finally {
          debouncedFunctions.delete(functionKey);
        }
      },
      wait,
      {
        leading: leadingEdge,
        trailing: !leadingEdge,
      },
    );

    const callingFn = async () => debouncedFn(mutation, variables, vyToastOptions, options);

    debouncedFunctions.set(functionKey, callingFn);
  }

  const debouncedMutation = getRequiredMapValue(debouncedFunctions, functionKey);

  return debouncedMutation();
}
